import { format } from 'date-fns';

import { CalendarIcon } from '@/components/Icons';

import { useEventCalendarViewState } from '../calendar-state';
import { useMeetingMinutesTabRows } from '../meeting-minutes-list-queries';
import { groupRowsByLocalDay } from '../meeting-minutes-list-utils';
import { MeetingMinutesEventCard } from './MeetingMinutesEventCard';

// Reading list for the Meeting Minutes tab (CC_CALENDAR_NOTES): the shared header's range,
// grouped by day, one card per note. Cards with a note expand in place to open it in the dock.
export const CalendarMeetingMinutesView = ({ anchorDate }: { anchorDate: Date }) => {
    const { eventCalendarViewState } = useEventCalendarViewState();
    const { notesFilter } = eventCalendarViewState;
    const { rows, fromDate, toDate } = useMeetingMinutesTabRows(anchorDate);

    // Only the first load shows the skeleton — afterwards useMeetingMinutesTabRows keeps the
    // previous range's rows visible while the next range fetches.
    if (!rows) {
        return <MeetingMinutesListSkeleton />;
    }

    const dayGroups = groupRowsByLocalDay(notesFilter === 'withNotes' ? rows.filter(({ note }) => note) : rows);

    if (dayGroups.length === 0) {
        const rangeText = `${format(fromDate, 'MMM d, yyyy')} - ${format(toDate, 'MMM d, yyyy')}`;
        return (
            <div className="text-muted-foreground flex min-h-0 flex-1 flex-col items-center justify-center py-8 text-center">
                <CalendarIcon className="mx-auto mb-4 h-12 w-12 opacity-50" />
                <p>
                    {notesFilter === 'withNotes'
                        ? `No events with notes between ${rangeText}`
                        : `No events between ${rangeText}`}
                </p>
            </div>
        );
    }

    return (
        <div className="min-h-0 flex-1 overflow-y-auto">
            {dayGroups.map(({ day, rows: dayRows }) => {
                // A meeting with several notes occupies several rows, so the gutter counts
                // distinct meetings rather than cards.
                const meetingCount = new Set(dayRows.map(({ event }) => event.eventGUID)).size;

                return (
                    <div
                        key={day.toISOString()}
                        className="grid grid-cols-[92px_1fr] gap-5 border-t py-3 first:border-t-0 first:pt-0"
                    >
                        <div>
                            <div className="text-cocoa-1000 text-2xl font-medium tabular-nums">
                                {format(day, 'dd EEE')}
                            </div>
                            <div className="text-muted-foreground mt-1 text-xs">
                                {meetingCount === 1 ? '1 meeting' : `${meetingCount} meetings`}
                            </div>
                        </div>
                        <div className="flex min-w-0 flex-col gap-1.5">
                            {dayRows.map(({ event, note }) => (
                                <MeetingMinutesEventCard
                                    key={note?.noteGUID ?? event.eventGUID}
                                    event={event}
                                    note={note}
                                />
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// Mirrors the day-row layout so the list doesn't shift when data arrives.
const MeetingMinutesListSkeleton = () => (
    <div className="min-h-0 flex-1 overflow-y-auto">
        {[0, 1].map((row) => (
            <div key={row} className="grid grid-cols-[92px_1fr] gap-5 border-t py-3 first:border-t-0 first:pt-0">
                <div className="flex flex-col items-end gap-2">
                    <div className="h-5 w-16 animate-pulse rounded bg-gray-200" />
                    <div className="h-3 w-12 animate-pulse rounded bg-gray-200" />
                </div>
                <div className="flex flex-col gap-1.5">
                    <div className="h-10 animate-pulse rounded-md bg-gray-200" />
                    <div className="h-10 animate-pulse rounded-md bg-gray-200" />
                </div>
            </div>
        ))}
    </div>
);
